import * as React from 'react';
import { Component } from 'react';
import { observer } from 'mobx-react';
import { inject } from '../utils';
import { withTheme } from 'styled-components';
import { styled } from '../utils/styled';
import { propStyles } from '../utils/propStyles';
import Translate from './Translate';
import Disclaimers from './Disclaimers';
import { DataStore, ConditionalDisclaimerStore } from '../stores';
import { computed } from 'mobx';
import HTag from './HTag';
import DocumentMenu from './Document/DocumentMenu';
import InlineDisclaimers from './InlineDisclaimers';
import { isNullOrEmpty } from '@kurtosys/ksys-app-template/dist/utils/typeChecks';
import { camelCase } from '@kurtosys/ksys-app-template/dist/utils/changeCase';

const DocumentModuleWrapper = styled<any, any>(
    'div',
    'documentModuleWrapper',
    'documentModule'
)`
    ${propStyles};
`;

DocumentModuleWrapper.defaultProps = {
    marginX: 'md',
};

const DocumentsList = styled<any, any>('ol', 'documentsList', 'documentModule')`
    ${propStyles};
    list-style: none;
`;

DocumentsList.defaultProps = {
    display: 'flex',
    flexDirection: 'column',
    marginY: 'md',
};

const DocumentsListItem = styled<any, any>(
    'li',
    'documentsListItem',
    'documentModule'
)`
    ${propStyles};
    list-style: none;
`;

DocumentsListItem.defaultProps = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingY: 'sm',
    borderWidth: '1px',
    borderColor: 'gray300',
    borderStyle: 'none none solid none',
};

const DocumentCategoryWrapper = styled<any, any>(
    'div',
    'documentCategoryWrapper',
    'documentModule'
)`
    ${propStyles};
`;
DocumentCategoryWrapper.defaultProps = {
    display: 'flex',
    flexDirection: ['column', 'row'],
};

const DocumentCategory = styled<any, any>(
    'div',
    'documentCategory',
    'documentModule'
)`
    ${propStyles};
`;

const DocumentCategoryDescription = styled<any, any>(
    'div',
    'documentCategoryDescription',
    'documentModule'
)`
    ${propStyles};
`;

const DocumentGroupDescription = styled<any, any>(
    'div',
    'documentGroupDescription',
    'documentModule'
)`
    ${propStyles};
`;

const DocumentGroupText = styled<any, any>(
    'div',
    'documentGroupText',
    'documentModule'
)`
    ${propStyles};
`;

DocumentCategory.defaultProps = {
    width: '100%',
};

interface DocumentModuleProps {
    theme?: { [key: string]: React.CSSProperties };
    dataStore?: DataStore;
}

@inject('dataStore')
@observer
class DocumentModule extends Component<DocumentModuleProps> {
    @computed
    get headerDisclaimer() {
        const {
            theme,
            dataStore: { documentModule },
        } = this.props;
        return (
            <Disclaimers
                disclaimers={documentModule.headerDisclaimers}
                theme={{
                    ...theme,
                    DisclaimersList: theme.DocumentModuleHeaderDisclaimersList,
                    DisclaimerListItem:
                        theme.DocumentModuleHeaderDisclaimerListItem,
                    DisclaimerContent:
                        theme.DocumentModuleHeaderDisclaimerContent,
                }}
            />
        );
    }

    @computed
    get noDataDisclaimer() {
        const {
            theme,
            dataStore: { documentModule },
        } = this.props;
        return (
            <Disclaimers
                disclaimers={documentModule.noDataDisclaimers}
                theme={{
                    ...theme,
                    DisclaimersList: theme.DocumentModuleNoDataDisclaimersList,
                    DisclaimerListItem:
                        theme.DocumentModuleNoDataDisclaimerListItem,
                    DisclaimerContent:
                        theme.DocumentModuleNoDataDisclaimerContent,
                }}
            />
        );
    }

    @computed
    get footerDisclaimer() {
        const {
            theme,
            dataStore: { documentModule },
        } = this.props;
        return (
            <Disclaimers
                disclaimers={documentModule.footerDisclaimers}
                theme={{
                    ...theme,
                    DisclaimersList: theme.DocumentModuleFooterDisclaimersList,
                    DisclaimerListItem:
                        theme.DocumentModuleFooterDisclaimerListItem,
                    DisclaimerContent:
                        theme.DocumentModuleFooterDisclaimerContent,
                }}
            />
        );
    }

    @computed
    get groupedDocuments() {
        const { theme, dataStore } = this.props;
        const { documentModule } = dataStore;
        const { groupedDocuments, config } = documentModule;
        const { showSingleDocument = true } = config;
        return (
            <DocumentsList {...theme.DocumentModuleList}>
                {groupedDocuments.map((d, index) => (
                    <DocumentsListItem
                        key={d.documentGroupName}
                        {...theme.DocumentModuleListItem}
                    >
                        <DocumentGroupText {...theme.DocumentModuleGroupText}>
                            <HTag
                                componentTheme={'DocumentModuleGroupName'}
                                defaultStyleProps={{
                                    fontFamily: 'body',
                                    fontWeight: 'normal',
                                    fontSize: 'sm',
                                    color: 'body',
                                    margin: '0 1em 0 0',
                                }}
                                level={3}
                                text={d.documentGroupName}
                            />
                            {d.description && (
                                <DocumentGroupDescription
                                    {...theme.DocumentModuleGroupDescription}
                                    dangerouslySetInnerHTML={{
                                        __html: d.description,
                                    }}
                                />
                            )}
                        </DocumentGroupText>
                        <DocumentMenu
                            documents={d.documents}
                            showCount={false}
                            showCheckbox={false}
                            showSingleDocument={showSingleDocument}
                            showSingleDocumentLabel={true}
                            theme={{
                                ...theme,
                                DocumentDropdownIcon: {
                                    margin: '0 0 0 0.5em',
                                },
                                DownloadControls:
                                    theme.DocumentModuleDownloadControls,
                                DownloadButton:
                                    theme.DocumentModuleDownloadButton,
                                DownloadButtonIcon: {
                                    marginX: '0',
                                    margin: '0 0 0 0.5em',
                                    ...theme.DocumentModuleDownloadButtonIcon,
                                },
                            }}
                            metricContext={`documentModule-${camelCase(
                                d.documentGroupName
                            )}`}
                        >
                            <Translate
                                placeholders={{
                                    total: d.documents.length,
                                }}
                            >
                                {`{total} Documents`}
                            </Translate>
                        </DocumentMenu>
                    </DocumentsListItem>
                ))}
            </DocumentsList>
        );
    }

    @computed
    get hasDocumentCategories() {
        const { dataStore } = this.props;
        const { documentModule } = dataStore;
        return !isNullOrEmpty(documentModule.documentCategories);
    }

    getDocumentConditionalDisclaimers(document) {
        let conditionalDisclaimers;
        if (document) {
            const { dataStore } = this.props;
            const {
                documentGroupMeta,
                documentGroupInferred,
            } = dataStore.config;
            let documentType: string = '';
            if (documentGroupInferred) {
                const key = documentGroupInferred;
                documentType = document.inferred[key];
            } else {
                const key = documentGroupMeta || 'document_type';
                documentType = document.meta[key].value;
            }

            if (dataStore && dataStore.disclaimerStore) {
                conditionalDisclaimers = ConditionalDisclaimerStore.getConditionalsForDocumentTypeAndCulture(
                    documentType
                );
            }
        }

        return conditionalDisclaimers;
    }

    @computed
    get categoryDocuments() {
        const { theme, dataStore } = this.props;
        const { documentModule } = dataStore;
        const { documentCategories } = documentModule;

        return (
            <DocumentCategoryWrapper {...theme.DocumentCategoryWrapper}>
                {documentCategories.map(documentCategory => {
                    return (
                        <DocumentCategory
                            key={documentCategory.heading}
                            {...theme.DocumentCategory}
                        >
                            <HTag
                                componentTheme={'DocumentCategoryHeading'}
                                defaultStyleProps={{
                                    display: 'block',
                                    marginX: 'sm',
                                    marginY: 'sm',
                                    fontFamily: 'heading',
                                    fontWeight: 'medium',
                                    fontSize: 'sm',
                                }}
                                level={2}
                                text={documentCategory.heading}
                            />
                            {documentCategory.description && (
                                <DocumentCategoryDescription
                                    {...theme.DocumentModuleCategoryDescription}
                                    dangerouslySetInnerHTML={{
                                        __html: documentCategory.description,
                                    }}
                                />
                            )}
                            <DocumentsList
                                key={documentCategory.heading}
                                {...theme.DocumentModuleList}
                            >
                                {documentCategory.groupedDocuments.map(
                                    (d, index) => (
                                        <DocumentsListItem
                                            key={d.documentGroupName}
                                            {...theme.DocumentModuleListItem}
                                        >
                                            <InlineDisclaimers
                                                accessibilityContext={{
                                                    key: `More information about the {label}`,
                                                    placeholders: {
                                                        label: d.documentGroupName
                                                    }
                                                }}
                                                conditionalDisclaimers={this.getDocumentConditionalDisclaimers(
                                                    d.documents &&
                                                    d.documents[0]
                                                )}
                                                analyticsContext={{
                                                    source: 'documentList',
                                                    sourceContext:
                                                        d.documents[0],
                                                }}
                                            >
                                                <HTag
                                                    componentTheme={
                                                        'DocumentModuleGroupName'
                                                    }
                                                    defaultStyleProps={{
                                                        fontFamily: 'body',
                                                        fontWeight: 'normal',
                                                        fontSize: 'sm',
                                                        color: 'body',
                                                        margin: '0 1em 0 0',
                                                    }}
                                                    level={3}
                                                    text={d.documentGroupName}
                                                />
                                            </InlineDisclaimers>
                                            <DocumentMenu
                                                documents={d.documents}
                                                showCount={false}
                                                showCheckbox={false}
                                                showSingleDocument={
                                                    d.showSingleDocument
                                                }
                                                showSingleDocumentLabel={true}
                                                theme={{
                                                    ...theme,
                                                    DocumentDropdownIcon: {
                                                        margin: '0 0 0 0.5em',
                                                    },
                                                    DownloadControls:
                                                        theme.DocumentModuleDownloadControls,
                                                    DownloadButton:
                                                        theme.DocumentModuleDownloadButton,
                                                    DownloadButtonIcon: {
                                                        marginX: '0',
                                                        margin: '0 0 0 0.5em',
                                                        ...theme.DocumentModuleDownloadButtonIcon,
                                                    },
                                                }}
                                                metricContext={`documentModule-${camelCase(
                                                    documentCategory.heading
                                                )}-${camelCase(
                                                    d.documentGroupName
                                                )}`}
                                            >
                                                <Translate
                                                    placeholders={{
                                                        total:
                                                            d.documents.length,
                                                    }}
                                                >
                                                    {`{total} Documents`}
                                                </Translate>
                                            </DocumentMenu>
                                        </DocumentsListItem>
                                    )
                                )}
                            </DocumentsList>
                        </DocumentCategory>
                    );
                })}
            </DocumentCategoryWrapper>
        );
    }

    render() {
        const { theme, dataStore } = this.props;
        const { stores, documentModule } = dataStore;
        return documentModule && !stores.loading.global ? (
            <DocumentModuleWrapper {...theme.DocumentModuleWrapper}>
                {this.headerDisclaimer}
                <HTag
                    componentTheme={'DocumentModuleHeading'}
                    defaultStyleProps={{
                        fontFamily: 'heading',
                        fontWeight: 'medium',
                        fontSize: 'sm',
                        color: 'body',
                    }}
                    level={2}
                    text={documentModule.header.label}
                />
                {documentModule.groupedDocuments.length > 0
                    ? this.hasDocumentCategories
                        ? this.categoryDocuments
                        : this.groupedDocuments
                    : this.noDataDisclaimer}
                {this.footerDisclaimer}
            </DocumentModuleWrapper>
        ) : null;
    }
}

export default withTheme(DocumentModule);



// WEBPACK FOOTER //
// ./src/Components/DocumentModule.tsx